<template>
  <div id="batchReading">
    <PageTitle :title="title" :subtitle="subtitle"></PageTitle>

    <PageContainer>
      <div class="container-title">{{$t('batch_reading.title')}}</div>
      <label class="container-description" v-html="$t('batch_reading.subtitle')"></label>
      <div class="batch-reading__body">
        <div class="table-container">
          <div class="label">
            {{ $t('batch_reading.total_sale') }}
          </div>
          <div class="table">
            <div class="table__icon">
              <div class="icon__container">
                <svgicon src="icon/pos_sales.svg" :height="20" :width="16"></svgicon>
              </div>
            </div>
            <div class="table__content">
              <div class="table__column">
                <div class="label">{{ $t('batch_reading.subtotal') }}</div>
                <div class="value">{{ $tc(subTotal) }}</div>
              </div>
              <div class="table__column">
                <div class="label">{{ $t('batch_reading.service_fees') }}</div>
                <div class="value value--small">{{ $tc(serviceFees) }}</div>
              </div>
              <div class="table__column">
                <div class="label">{{ $t('batch_reading.surcharge_fees') }}</div>
                <div class="value value--small">{{ $tc(surchargeFees) }}</div>
              </div>
              <div class="table__column">
                <div class="label">{{ $t('batch_reading.cash_rounding') }}</div>
                <div class="value value--small">{{ $tc(cashRounding) }}</div>
              </div>
              <div class="table__column">
                <div class="label">{{ $t('batch_reading.tip') }}</div>
                <div class="value value--small">{{ $tc(tipTotal) }}</div>
              </div>
              <div class="table__column">
                <div class="label">{{ $t('batch_reading.total') }}</div>
                <div class="value">{{ $tc(total) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="table-container" v-if="salesDataByService.length > 0">
          <div class="label">
            {{ $t('batch_reading.total_sale') + " / " + $t('batch_reading.app')}}
          </div>
          <div class="table">
            <div class="table__icon">
              <div class="icon__container">
                <svgicon src="icon/online.svg" :height="20" :width="16"></svgicon>
              </div>
            </div>
            <div class="table__content">
              <div class="table__column" v-for="service of salesDataByService" :key="service.service">
                <div class="label">{{ $t('batch_reading.services.' + service.service) }}</div>
                <div class="value">{{ $tc(service.total) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="table-container" v-if="salesDataByPaymentMethod.length > 0">
          <div class="label">
            {{ $t('batch_reading.total_sale') + " / " + $t('batch_reading.payment_method')}}
          </div>
          <div class="table">
            <div class="table__icon">
              <div class="icon__container">
                <svgicon src="icon/pos_payment.svg" :height="19" :width="20"></svgicon>
              </div>
            </div>
            <div class="table__content">
              <div class="table__column" v-for="payment of salesDataByPaymentMethod" :key="payment.type">
                <div class="label">{{ $t('batch_reading.payment_methods.' + payment.type) }}</div>
                <div class="value">{{ $tc(payment.settledAmount) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="table-container">
          <div class="label">
            {{ $t('batch_reading.invoices') }}
          </div>
          <div class="table">
            <div class="table__icon">
              <div class="icon__container">
                <svgicon src="icon/pos_invoice.svg" :height="20" :width="18"></svgicon>
              </div>
            </div>
            <div class="table__content">
              <div class="table__column">
                <div class="label">{{ $t('batch_reading.total') }}</div>
                <div class="value">{{ invoiceCount }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>

    <PageFooter :backAction="{action: back, text: $t('batch_reading.back_to_batches')}"></PageFooter>
  </div>
</template>

<script>
import PageTitle from "@/components/PageTitle";
import PageContainer from "@/components/PageContainer";
import PageFooter from "@/components/PageFooter";
import {mapActions} from "vuex";
import moment from "moment-timezone";

const MOMENT_API_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
const MOMENT_DATE_TIME_FORMAT = 'YYYY-MM-DD, hh:mm A';
export default {
  name: "BatchReading",

  components: { PageTitle, PageContainer, PageFooter },

  data() {
    return {
      batch: null
    }
  },

  async mounted() {
    this.batch = await this.getBatch({ batchId: this.$route.params.id});
  },
  computed: {
    title() {
      let title = "[" + this.$route.params.id + "]";
      if (this.batch) {
        if (this.batch.workstation && this.batch.workstation.id) {
          title = title + " " + this.batch.workstation.id;
        } else if (this.batch.employee) {
          title = title + " " + this.getFullName(this.batch.employee)
        }
      }
      return title;
    },
    subtitle() {
      return this.$t('batch_reading.status') + ": " + this.$t(this.status) + " | " +  this.$t('batch_reading.last_update') + ": " + this.lastUpdate;
    },
    status() {
      if (this.batch && this.batch.closedAt) {
        return "closed";
      }
      return "open";
    },
    lastUpdate() {
      if (this.batch) {
        if (this.batch.closedAt) {
          return moment(this.batch.closedAt, MOMENT_API_DATE_TIME_FORMAT).format(MOMENT_DATE_TIME_FORMAT);
        }
        return moment(this.batch.start, MOMENT_API_DATE_TIME_FORMAT).format(MOMENT_DATE_TIME_FORMAT);
      }
      return this.$t('unknown');
    },
    salesDataByPaymentMethod() {
      if (this.batch && this.batch.settlementByType) {
        return this.batch.settlementByType;
      }
      return [];
    },
    salesDataByService() {
      if (this.batch && this.batch.invoiceByService) {
        return this.batch.invoiceByService;
      }
      return [];
    },
    total() {
      if (this.batch && this.batch.total) {
        return this.batch.total;
      }
      return 0;
    },
    subTotal() {
      if (this.batch && this.batch.subTotal) {
        return this.batch.subTotal;
      }
      return 0;
    },
    serviceFees() {
      if (this.batch && this.batch.serviceFees) {
        return this.batch.serviceFees;
      }
      return 0;
    },
    surchargeFees() {
      if (this.batch && this.batch.surchargeFees) {
        return this.batch.surchargeFees;
      }
      return 0;
    },
    cashRounding() {
      if (this.batch && this.batch.cashRounding) {
        return this.batch.cashRounding;
      }
      return 0;
    },
    tipTotal() {
      if (this.batch && this.batch.tipTotal) {
        return this.batch.tipTotal;
      }
      return 0;
    },
    depositsTotal() {
      if (this.batch && this.batch.deposits && this.batch.deposits.length > 0) {
        return this.batch.deposits; // TODO i need the fields to sum the deposits
      }
      return 0;
    },
    invoiceCount() {
      if (this.batch && this.batch.invoiceCount) {
        return this.batch.invoiceCount;
      }
      return 0;
    }
  },
  methods: {
    ...mapActions(["getBatch", "getCompany"]),

    back() {
      this.$router.push(this.$router.buildContext("/batch-reading"));
    },
    getFullName(e) {
      let fullName = `${e.lastName} ${e.firstName}`.trim();
      let publicName = typeof e.publicName === 'string' ? e.publicName.trim() : null;
      return publicName ? `${fullName} (${publicName})` : fullName;
    },
  }
}
</script>

<style lang="scss" scoped>
  #batchReading {

    .container-title {
      font-size: 16px;
      font-weight: 700;
      color: #1a1a1a;
    }
    .container-description {
      color: #808080;
      margin-bottom: 20px;
      font-size: 14px;
      line-height: 1;
    }
    .label {
      color: #808080;
      font-size: 14px;
    }
    .batch-reading__body {
      display: flex;
      flex-wrap: wrap;
    }
    .table-container {
      padding-bottom: 16px;
      display: flex;
      flex-direction: column;
    }
    .table {
      padding: 12px;
      border-radius: 20px;
      box-shadow: 0px 1px 6px #0000001A;
      background-color: #FFFFFF;
      margin: 6px 20px 6px 0;
      display: flex;
      //flex-direction: column;
      width: 300px;

      .table__icon {
        padding-right: 12px;
        .icon__container {
          display: flex;
          width: 40px;
          height: 40px;
          background-color: #F5F5F5;
          border-radius: 6px;
          fill: #CCCCCC;
          justify-content: center;
          align-items: center;
        }
      }

      .table__content {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }

      .table__column {
        flex-grow: 1;
        padding: 13px 0 8px 0;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;

        &:not(:last-child) {
          border-bottom: 1px solid #E6E6E6;
        }
      }

      .value {
        font-size: 16px;
        font-weight: 900;
      }

      .value--small {
        font-size: 16px;
        font-weight: normal;
      }
    }

  }
</style>